import { ReactNode, VFC, MouseEvent, useState } from 'react'
import { Menu, PopoverOrigin } from '@mui/material'
import {
  ExpandMore,
  Check,
  ExpandLess,
  SvgIconComponent,
} from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import theme from 'lib/theme'
import {
  DropdownText,
  IconButtonWrapper,
  IconDropdownText,
} from './IconDropdown.styled'

export interface DropdownOption {
  label: string
  value: string
}

interface IconDropdownProps {
  height?: string
  anchorOrigin?: PopoverOrigin
  transformOrigin?: PopoverOrigin
  minWidth?: string
  expandIconRequired?: boolean
  options: DropdownOption[]
  renderOption?: (option: DropdownOption) => ReactNode
  onChange: (selectedValue: string, innerValue: string) => void
  ariaLabel: string
  isIconOnly?: boolean
  optionItem?: string
  Icon: SvgIconComponent
  disabled?: boolean
  translateLabel?: boolean
}

const IconDropdown: VFC<IconDropdownProps> = ({
  height,
  anchorOrigin,
  transformOrigin,
  minWidth,
  expandIconRequired,
  options,
  onChange,
  ariaLabel,
  isIconOnly,
  optionItem,
  Icon,
  disabled,
  translateLabel,
}) => {
  const { t } = useTranslation()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)
  const handleClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }
  const handleMenuItemClick = (
    event: MouseEvent<HTMLLIElement>,
    value: string
  ) => {
    onChange(event.currentTarget.innerText, value)
    setAnchorEl(null)
  }
  const handleClose = () => {
    setAnchorEl(null)
  }

  return (
    <div>
      <IconButtonWrapper
        isIconOnly={Boolean(isIconOnly)}
        isOpen={open}
        aria-label={ariaLabel}
        id='long-button'
        aria-controls={open ? 'long-menu' : undefined}
        aria-expanded={open}
        aria-haspopup='true'
        onClick={handleClick}>
        <Icon sx={{ fill: anchorEl && 'black' }} fontSize='medium' />
        {!isIconOnly && (
          <DropdownText
            px={2}
            sx={{
              color: anchorEl && 'black',
            }}>
            {optionItem}
          </DropdownText>
        )}
        {expandIconRequired &&
          (anchorEl ? (
            <ExpandLess fontSize='small' />
          ) : (
            <ExpandMore fontSize='small' />
          ))}
      </IconButtonWrapper>
      <Menu
        id='long-menu'
        MenuListProps={{
          'aria-labelledby': 'long-button',
        }}
        transformOrigin={
          transformOrigin || { vertical: 'top', horizontal: 'left' }
        }
        anchorOrigin={
          anchorOrigin || { vertical: 'bottom', horizontal: 'left' }
        }
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          style: {
            maxHeight: height || 48 * 4.5,
            minWidth,
            borderRadius: '1px',
          },
        }}
        sx={{ ul: { padding: 0 } }}>
        {options.map((option) => (
          <IconDropdownText
            disabled={disabled}
            key={option.value}
            value={option.value}
            selected={option.label === optionItem}
            onClick={(e) => handleMenuItemClick(e, option.value)}>
            {translateLabel ? t(option.label) : option.label}
            {option.label === optionItem && (
              <Check
                fontSize='small'
                sx={{ fill: theme.palette.primary.main }}
              />
            )}
          </IconDropdownText>
        ))}
      </Menu>
    </div>
  )
}

export default IconDropdown
